import React, { useEffect, useMemo, useRef, useState } from 'react'
import phoneIcon from '../../img/phoneIcon.svg'
import messageIcon from '../../img/messageIcon.svg'
import '../../../styles/close-ticket.css'
import DefaultCloseForm         from '../../../features/tickets/forms/DefaultCloseForm'
import DcoCloseForm             from '../../../features/tickets/forms/DcoCloseForm'
import AfterSalesCloseForm      from '../../../features/tickets/forms/AfterSalesCloseForm'
import StationCloseForm         from '../../../features/tickets/forms/StationCloseForm'
import AarCloseForm             from '../../../features/tickets/forms/AarCloseForm'
import CustomerSupportCloseForm from '../../../features/tickets/forms/CustomerSupportCloseForm'
import ErtCloseForm             from '../../../features/tickets/forms/ErtCloseForm'

const TABS = [
  { id: 'overview',       label: 'Overview' },
  { id: 'timeline',       label: 'Timeline' },
  { id: 'communications', label: 'Communications' },
  { id: 'evidence',       label: 'Evidence' },
]

function resolveCloseForm(team) {
  if (team === 'dco')              return DcoCloseForm
  if (team === 'after_sales')      return AfterSalesCloseForm
  if (team === 'station')          return StationCloseForm
  if (team === 'aar')              return AarCloseForm
  if (team === 'customer_support') return CustomerSupportCloseForm
  if (team === 'ert')              return ErtCloseForm
  return DefaultCloseForm
}

const formatFieldLabel = (key) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

const STATUS_OPTIONS = [
  { value: 'in_progress', label: 'In Progress' },
  { value: 'on_hold',     label: 'On Hold' },
  { value: 'closed',      label: 'Closed' },
  { value: 're_opened',   label: 'Re-Opened' },
]

const displayStatus = (s) => {
  if (!s) return '—'
  const map = { in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed', on_hold: 'On Hold', re_opened: 'Re-Opened' }
  return map[s] || s
}
const displayPriority = (p) => p ? p.charAt(0).toUpperCase() + p.slice(1) : null
const displayChannel = (c) => {
  if (!c) return '—'
  const map = { call: 'Phone Call (IVR)', whatsapp: 'WhatsApp', rider_app: 'Rider App', email: 'Email', chat: 'Chat' }
  return map[c] || c.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase())
}
const statusClass = (s) => {
  if (s === 'in_progress') return 'in-progress'
  if (s === 'resolved')    return 'resolved'
  if (s === 'on_hold')     return 'on-hold'
  if (s === 'closed')      return 'closed'
  if (s === 're_opened')   return 're-opened'
  return 'in-progress'
}
const formatExperience = (v) => {
  if (!v) return '—'
  const map = { '0_15_days': '<15 days', less_than_5_years: '<5yrs', '5_plus_years': '5+ yrs' }
  return map[v] || v.replace(/_/g, ' ')
}
const formatConfidence = (v) => {
  if (!v) return null
  const n = String(v).toLowerCase()
  if (n.includes('high'))   return { label: 'High',   className: 'high' }
  if (n.includes('medium')) return { label: 'Medium', className: 'medium' }
  if (n.includes('low'))    return { label: 'Low',    className: 'low' }
  return { label: v, className: 'medium' }
}
const formatRisk = (v) => {
  if (!v) return null
  const n = String(v).toLowerCase()
  if (n.includes('low'))  return { label: 'Low',  className: 'low-risk' }
  if (n.includes('high')) return { label: 'High', className: 'high-risk' }
  return { label: v, className: 'medium' }
}
const dash = (v) => (v === null || v === undefined || v === '') ? '—' : v

const TICKET_ID_PARAM = 'ticketId'
function getTicketIdFromUrl() {
  if (typeof window === 'undefined') return null
  return new URLSearchParams(window.location.search).get(TICKET_ID_PARAM) || null
}

function formatMins(mins) {
  if (mins === null || mins === undefined) return '—'
  const m = Math.round(Math.abs(mins))
  const h = Math.floor(m / 60)
  const rem = m % 60
  return h > 0 ? `${h}h ${rem}m` : `${m}m`
}

function timelineColor(title) {
  const t = (title || '').toLowerCase()
  if (t.includes('creat'))                            return '#3B82F6'
  if (t.includes('assign'))                           return '#10B981'
  if (t.includes('progress'))                         return '#F59E0B'
  if (t.includes('hold'))                             return '#6B7280'
  if (t.includes('resolv'))                           return '#22C55E'
  if (t.includes('clos'))                             return '#94A3B8'
  if (t.includes('re-open') || t.includes('reopen')) return '#EF4444'
  return '#9CA3AF'
}

function InfoRow({ label, value, children }) {
  return (
    <div className="td-row">
      <span className="td-row-label">{label}</span>
      <span className="td-row-value">{children || dash(value)}</span>
    </div>
  )
}

function SectionCard({ title, children }) {
  return (
    <div className="td-card">
      <div className="td-card-title">{title}</div>
      {children}
    </div>
  )
}

function TatDetails({ tat, stage }) {
  const remaining = tat?.remaining_minutes ?? null
  const total     = tat?.tat_minutes       ?? null
  const elapsed   = tat?.elapsed_minutes   ?? null
  const level     = (tat?.level || stage || 'l1').toUpperCase()
  const progress  = (total > 0 && elapsed !== null)
    ? Math.min(100, Math.max(0, (elapsed / total) * 100))
    : 0
  const isOverdue = remaining !== null && remaining <= 0

  return (
    <div className="td-card td-card-sidebar td-sla-card">
      <div className="td-card-title">SLA Details</div>
      <div className="td-sla-timer" style={{ color: isOverdue ? '#DC2626' : '#D97706' }}>
        {remaining !== null ? formatMins(remaining) : '—'}
        <span style={{ fontSize: 12, fontWeight: 400, marginLeft: 6, color: '#6B7280' }}>remaining</span>
      </div>
      <div className="td-sla-bar">
        <div className="td-sla-bar-fill"
          style={{ width: `${progress}%`, background: isOverdue ? '#DC2626' : '#F59E0B' }} />
      </div>
      <div className="td-sla-meta-row">
        <span><span>Level:</span> {level}</span>
        <span><span>SLA:</span> {formatMins(total)}</span>
      </div>
      <div className="td-sla-meta-row">
        <span><span>Elapsed:</span> {formatMins(elapsed)}</span>
        <span><span>Remaining:</span> {formatMins(remaining)}</span>
      </div>
    </div>
  )
}

function QuickActions({ status, ticketId, fetchJson, onStatusUpdated }) {
  const [qaStatus, setQaStatus] = useState(status || 'in_progress')
  const [comment,  setComment]  = useState('')
  const [posting,  setPosting]  = useState(false)
  const [postError, setPostError] = useState('')

  useEffect(() => { setQaStatus(status || 'in_progress') }, [status])

  const handlePost = async () => {
    if (!comment.trim() || !fetchJson || !ticketId) return
    setPosting(true)
    setPostError('')
    try {
      await fetchJson(`/api/tickets/ticket/update-status/${ticketId}/`, {
        method: 'PUT',
        body: JSON.stringify({ status: qaStatus, comment }),
      })
      onStatusUpdated(qaStatus)
      setComment('')
    } catch (err) {
      setPostError(err?.message || 'Failed to update status')
    } finally {
      setPosting(false)
    }
  }

  return (
    <div className="td-card td-card-sidebar td-qa-card">
      <div className="td-card-title">Quick Actions</div>
      <label className="td-qa-label" htmlFor="ta-qa-status">Update Status</label>
      <select
        id="ta-qa-status"
        className="td-qa-select"
        value={qaStatus}
        onChange={e => setQaStatus(e.target.value)}
      >
        {STATUS_OPTIONS.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <textarea
        className="td-qa-textarea"
        placeholder="Type comment..."
        value={comment}
        onChange={e => setComment(e.target.value)}
      />
      {postError && (
        <p style={{ fontSize: 11, color: '#DC2626', margin: '4px 0 0' }}>{postError}</p>
      )}
      <button
        type="button"
        className="td-qa-post"
        disabled={!comment.trim() || posting}
        onClick={handlePost}
      >
        {posting ? 'Posting…' : 'Post'}
      </button>
    </div>
  )
}

export default function TicketAssignmentsDetail({ ticket, setCurrentPage, fetchJson, setSelectedVehicle }) {
  const [detail,            setDetail]           = useState(null)
  const [loading,           setLoading]          = useState(true)
  const [error,             setError]            = useState(null)
  const [activeTab,         setActiveTab]        = useState('overview')
  const [statusValue,       setStatusValue]      = useState('in_progress')
  const [statusUpdating,    setStatusUpdating]   = useState(false)
  const [detailRefresh,     setDetailRefresh]    = useState(0)
  const [closeSubmitting,   setCloseSubmitting]  = useState(false)
  const [closeError,        setCloseError]       = useState('')
  const [closeDone,         setCloseDone]        = useState(false)
  const [closeFormOpen,     setCloseFormOpen]    = useState(true)
  const didInitClosePanel   = useRef(false)
  const [attachments,         setAttachments]        = useState([])
  const [attachmentsLoading,  setAttachmentsLoading] = useState(false)
  const [evidenceFiles,       setEvidenceFiles]      = useState([])
  const [evidenceUploading,   setEvidenceUploading]  = useState(false)
  const [evidenceUploadError, setEvidenceUploadError] = useState('')
  const [evidenceRefresh,     setEvidenceRefresh]    = useState(0)
  const evidenceFileInputRef  = useRef(null)
  const [resolutionAttachments,        setResolutionAttachments]        = useState([])
  const [resolutionAttachmentsLoading, setResolutionAttachmentsLoading] = useState(false)

  const ticketId = ticket?.ticket_id ?? getTicketIdFromUrl()

  useEffect(() => {
    if (!ticket?.ticket_id || typeof window === 'undefined') return
    const url = new URL(window.location.href)
    if (url.searchParams.get(TICKET_ID_PARAM) !== String(ticket.ticket_id)) {
      url.searchParams.set(TICKET_ID_PARAM, String(ticket.ticket_id))
      window.history.replaceState({}, '', url.toString())
    }
  }, [ticket?.ticket_id])

  /* fetch ticket detail */
  useEffect(() => {
    if (!ticketId) { setLoading(false); setError('Ticket not found.'); return }
    if (!fetchJson) return
    let active = true
    setLoading(true); setError(null); setDetail(null)
    fetchJson(`/api/tickets/ticket/assigned-ticket/${ticketId}/`)
      .then(data => {
        if (!active) return
        setDetail(data)
        const s = data?.ticket_information?.status
        if (s) setStatusValue(s)
        setCloseDone(false)
      })
      .catch(err => {
        if (!active) return
        setError(err?.status === 403 || err?.status === 404
          ? "You don't have access to this ticket."
          : 'Failed to load ticket details.')
      })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [ticketId, fetchJson, detailRefresh])

  const info    = detail?.ticket_information || {}
  const rider   = detail?.rider_details      || {}
  const vehicle = detail?.vehicle_details    || {}
  const tat     = detail?.tat_details        || null
  const timelines      = detail?.ticket_timelines      || []
  const communications = detail?.ticket_communications || []
  const resolutionData     = detail?.resolution_data || null
  const hasResolutionData  = !!(resolutionData && Object.keys(resolutionData).length > 0)
  const isTicketClosed     = statusValue === 'closed'
  const showReadOnly       = hasResolutionData && isTicketClosed
  const resolutionTeam     = detail?.team || null
  const CloseFormComponent = resolveCloseForm(resolutionTeam)

  /* Auto-collapse close panel only when ticket is closed with resolution data */
  useEffect(() => {
    if (didInitClosePanel.current || !detail) return
    didInitClosePanel.current = true
    if (showReadOnly) setCloseFormOpen(false)
  }, [detail, showReadOnly])

  /* Reset per-ticket state when a different ticket is viewed */
  useEffect(() => {
    didInitClosePanel.current = false
    setCloseFormOpen(true)
    setCloseDone(false)
    setCloseError('')
  }, [ticketId])

  const handleCloseSubmit = async (payload) => {
    if (!fetchJson || !ticketId) return
    setCloseError('')
    setCloseSubmitting(true)
    try {
      const isFormData = payload instanceof FormData
      await fetchJson(`/api/tickets/ticket/resolve-ticket/${ticketId}/`, {
        method: 'POST',
        body: isFormData ? payload : JSON.stringify(payload),
      })
      setCloseDone(true)
      setDetailRefresh(n => n + 1)
    } catch (err) {
      setCloseError(err?.message || 'Something went wrong. Please retry.')
    } finally {
      setCloseSubmitting(false)
    }
  }

  /* fetch attachments when Evidence tab opens */
  useEffect(() => {
    if (activeTab !== 'evidence' || !ticketId || !fetchJson) return
    setAttachmentsLoading(true)
    fetchJson(`/api/tickets/ticket/get-attachments/${ticketId}/?attachment_type=create_ticket`)
      .then(data => setAttachments(Array.isArray(data) ? data : []))
      .catch(() => setAttachments([]))
      .finally(() => setAttachmentsLoading(false))
  }, [activeTab, ticketId, fetchJson, evidenceRefresh])

  /* fetch resolution attachments when close panel opens (only after resolution filled) */
  useEffect(() => {
    if (!closeFormOpen || !hasResolutionData || !ticketId || !fetchJson) return
    const attachmentType = resolutionData?.attachment_type
    if (!attachmentType) { setResolutionAttachments([]); return }
    setResolutionAttachmentsLoading(true)
    fetchJson(`/api/tickets/ticket/get-attachments/${ticketId}/?attachment_type=${attachmentType}`)
      .then(data => setResolutionAttachments(Array.isArray(data) ? data : []))
      .catch(() => setResolutionAttachments([]))
      .finally(() => setResolutionAttachmentsLoading(false))
  }, [closeFormOpen, hasResolutionData, ticketId, fetchJson, resolutionData?.attachment_type])

  const handleHeaderStatusChange = async (newStatus) => {
    if (!fetchJson || !ticketId || statusUpdating) return
    setStatusUpdating(true)
    try {
      await fetchJson(`/api/tickets/ticket/update-status/${ticketId}/`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus }),
      })
      setStatusValue(newStatus)
      setDetailRefresh(n => n + 1)
    } catch { }
    finally { setStatusUpdating(false) }
  }

  const ticketNumber = info.ticket_number || ticket?.ticket_number
  const vin = vehicle.vin_number || ticket?.vin_number
  const subtitle = useMemo(() => {
    const parts = []
    if (info.sub_category || info.category) parts.push(info.sub_category || info.category)
    if (vin) parts.push(`VIN: ${vin}`)
    return parts.join(' — ')
  }, [info.sub_category, info.category, vin])

  const confidence = formatConfidence(rider.identity_confidence)
  const theftRisk  = formatRisk(rider.theft_risk)
  const copyVin    = () => vin && navigator.clipboard?.writeText(vin)
  const goToVehicle360 = () => {
    if (!vin || !setSelectedVehicle || !setCurrentPage) return
    fetchJson(`/api/vehicles/search-bar?q=${encodeURIComponent(vin)}`)
      .then(data => {
        const v = data?.vehicle_details
        setSelectedVehicle({
          vehicle_id: v?.id || '',
          vin_number: v?.vin || vin,
          registration_number: v?.registration_number || vehicle.registration_number || '',
        })
        setCurrentPage('vehicle-360')
      })
      .catch(() => {
        setSelectedVehicle({ vehicle_id: '', vin_number: vin, registration_number: vehicle.registration_number || '' })
        setCurrentPage('vehicle-360')
      })
  }

  if (loading) return <div className="td-page td-feedback">Loading ticket details…</div>

  if (error || !detail) {
    return (
      <div className="td-page td-feedback error">
        {error || 'Failed to load ticket details.'}
        <div style={{ marginTop: 12 }}>
          <button type="button" className="td-btn td-btn-ghost" onClick={() => setCurrentPage('ticket_assignments')}>
            Back to Ticket Assignments
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="td-page">
      <nav className="td-breadcrumb" aria-label="Breadcrumb">
        <button type="button" onClick={() => setCurrentPage('ticket_assignments')}>Ticket Assignments</button>
        <span className="td-breadcrumb-sep">›</span>
        <span className="td-breadcrumb-current">{ticketNumber}</span>
      </nav>

      <div className="td-header-box">
        <div className="td-header-box-left">
          <div className="td-header-top-line">
            <h1 className="td-title">{ticketNumber}</h1>
            <div className="td-badges">
              {statusValue && (
                <span className={`td-badge status ${statusClass(statusValue)}`}>
                  <span className="td-badge-dot" />
                  {displayStatus(statusValue)}
                </span>
              )}
              {info.priority && (
                <span className={`td-badge priority ${info.priority}`}>{displayPriority(info.priority)}</span>
              )}
              {info.ticket_stage && (
                <span className="td-badge stage">{String(info.ticket_stage).toUpperCase()}</span>
              )}
            </div>
          </div>
          <p className="td-subtitle">{subtitle}</p>
        </div>

        <div className="td-header-box-actions">
          <button type="button" className="td-btn td-btn-outline-blue" style={{ display: 'none' }}>
            <img src={phoneIcon} alt="" /> Call Rider
          </button>
          <button type="button" className="td-btn td-btn-outline-green" style={{ display: 'none' }}>
            <img src={messageIcon} alt="" /> WhatsApp
          </button>
          <div className="td-status-select-wrap">
            <select
              className="td-status-select"
              value={statusValue}
              onChange={e => handleHeaderStatusChange(e.target.value)}
              disabled={statusUpdating}
              aria-label="Ticket status"
            >
              {STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <span className="td-status-select-arrow">{statusUpdating ? '…' : '▼'}</span>
          </div>
          <button type="button" className="td-btn td-btn-ghost" style={{ display: 'none' }}>Reassign</button>
        </div>
      </div>

      <div className="td-body-layout">
        <div className="td-body-main">
          <div className="td-tabs" role="tablist">
            {TABS.map(tab => (
              <button
                key={tab.id} type="button" role="tab"
                aria-selected={activeTab === tab.id}
                className={`td-tab${activeTab === tab.id ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* ── Overview ── */}
          {activeTab === 'overview' && (
            <div className="td-main">
              <SectionCard title="Ticket Information">
                <InfoRow label="Ticket ID" value={ticketNumber} />
                <InfoRow label="Status">
                  <span className={`td-status-inline td-badge status ${statusClass(statusValue)}`}>
                    <span className="td-badge-dot" />{displayStatus(statusValue)}
                  </span>
                </InfoRow>
                <InfoRow label="Priority">
                  {info.priority
                    ? <span className={`td-priority-inline ${info.priority}`}>{displayPriority(info.priority)}</span>
                    : '—'}
                </InfoRow>
                <InfoRow label="Category"     value={info.category} />
                <InfoRow label="Sub-Category" value={info.sub_category} />
                <InfoRow label="CNC transaction id / CNC error code"
                  value={info.cnc_transaction_id || info.cnc_error_code
                    ? [info.cnc_transaction_id, info.cnc_error_code].filter(Boolean).join(' / ')
                    : '—'}
                />
                <InfoRow label="Description"  value={info.description} />
                <InfoRow label="Channel"      value={displayChannel(info.channel)} />
                <InfoRow label="Station ID"   value={info.station_code || info.station_id} />
                <InfoRow label="Ticket Created Date"
                  value={info.created_time && info.ticket_created_date
                    ? `${info.ticket_created_date} · ${info.created_time}`
                    : info.ticket_created_date || info.created_time}
                />
                <InfoRow label="Assigned To"      value={info.assigned_to} />
                <InfoRow label="Ticket raised by" value={info.ticket_raised_by || info.created_by} />
                <InfoRow label="Ticket stage"
                  value={info.ticket_stage ? String(info.ticket_stage).toUpperCase() : null} />
                <InfoRow label="Closing TAT"      value={info.closing_tat} />
                <InfoRow label="Type of Ticket"   value={info.ticket_type ? String(info.ticket_type).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null} />
              </SectionCard>

              <SectionCard title="Rider Details">
                <InfoRow label="Rider Name"       value={rider.name} />
                <InfoRow label="Rider Mobile"     value={rider.number} />
                <InfoRow label="Preferred Channel" value={displayChannel(rider.preferred_channel)} />
                <InfoRow label="Language"         value={rider.language} />
                <InfoRow label="Customer Type"    value={rider.customer_type || info.customer_type} />
                <InfoRow label="Rider Experience" value={formatExperience(rider.rider_experience)} />
              </SectionCard>

              <SectionCard title="Vehicle Details">
                <div className="td-row">
                  <span className="td-row-label">VIN</span>
                  <span className="td-row-value">
                    {vin ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <button type="button" className="td-vin-link" onClick={goToVehicle360} title="Open Vehicle 360">
                          {vin}
                        </button>
                        <button type="button" className="td-copy-icon" onClick={copyVin} title="Copy VIN" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}>⧉</button>
                      </span>
                    ) : '—'}
                  </span>
                </div>
                <InfoRow label="Reg No."         value={vehicle.registration_number} />
                <InfoRow label="Type of Vehicle" value={vehicle.vehicle_type || vehicle.type_of_vehicle} />
                <InfoRow label="Vehicle status">
                  {vehicle.vehicle_status ? (
                    <span className="td-vehicle-status">
                      <span className="td-vehicle-status-dot" />{vehicle.vehicle_status}
                    </span>
                  ) : '—'}
                </InfoRow>
                <InfoRow label="Current Battery ID" value={vehicle.current_battery_id} />
              </SectionCard>
            </div>
          )}

          {/* ── Timeline ── */}
          {activeTab === 'timeline' && (
            <div style={{ padding: '20px 4px' }}>
              {timelines.length === 0 ? (
                <div className="td-tab-placeholder">No timeline events yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {timelines.map((event, i) => (
                    <div key={i} style={{ display: 'flex', gap: 16, position: 'relative', paddingBottom: 24 }}>
                      {i < timelines.length - 1 && (
                        <div style={{ position: 'absolute', left: 7, top: 18, bottom: 0, width: 2, background: '#E2E8F0' }} />
                      )}
                      <div style={{
                        width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                        background: timelineColor(event.title),
                        boxShadow: `0 0 0 3px ${timelineColor(event.title)}22`,
                      }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#0F172A', marginBottom: 2 }}>{event.title || '—'}</div>
                        {event.description && <div style={{ fontSize: 13, color: '#4B5563', marginBottom: 4 }}>{event.description}</div>}
                        {event.time && <div style={{ fontSize: 12, color: '#94A3B8' }}>{event.time}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Communications ── */}
          {activeTab === 'communications' && (
            <div style={{ padding: '20px 4px' }}>
              {communications.length === 0 ? (
                <div className="td-tab-placeholder">No communications yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {communications.map((comm, i) => {
                    const title = comm.title || ''
                    const isCall = title.toLowerCase().includes('call')
                    const isWhatsApp = title.toLowerCase().includes('whatsapp')
                    const isEmail = title.toLowerCase().includes('email')
                    const icon = isCall ? '📞' : isWhatsApp ? '💬' : isEmail ? '✉️' : '📱'
                    const statusColor =
                      (comm.status || '').toLowerCase() === 'delivered' ? '#16A34A' :
                      (comm.status || '').toLowerCase() === 'failed'    ? '#DC2626' :
                      (comm.status || '').toLowerCase() === 'pending'   ? '#D97706' :
                      '#6B7280'
                    return (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '12px 16px', background: '#fff',
                        border: '1px solid #E2E8F0', borderRadius: 10,
                        boxShadow: '0 1px 3px rgba(0,0,0,.04)',
                      }}>
                        <div style={{ width: 36, height: 36, borderRadius: 8, flexShrink: 0, background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{icon}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#1E293B', marginBottom: 2 }}>{title || '—'}</div>
                          <div style={{ fontSize: 12, color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {[comm.description, comm.time].filter(Boolean).join(' — ')}
                          </div>
                        </div>
                        {comm.status && (
                          <span style={{ fontSize: 12, fontWeight: 600, color: statusColor, flexShrink: 0, paddingLeft: 8 }}>
                            {comm.status.charAt(0).toUpperCase() + comm.status.slice(1)}
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Evidence (attachments) ── */}
          {activeTab === 'evidence' && (
            <div style={{ padding: '20px 4px' }}>
              <div style={{ marginBottom: 20, padding: '14px 16px', background: '#F8FAFC', border: '1.5px dashed #CBD5E1', borderRadius: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>Upload Evidence</div>
                <input ref={evidenceFileInputRef} type="file" multiple style={{ display: 'none' }}
                  onChange={e => {
                    const files = Array.from(e.target.files || [])
                    if (files.length) setEvidenceFiles(prev => [...prev, ...files])
                    e.target.value = ''
                  }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => evidenceFileInputRef.current?.click()} disabled={evidenceUploading}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', border: '1.5px solid #CBD5E1', borderRadius: 7, background: '#fff', cursor: 'pointer', fontSize: 12, color: '#64748B', fontWeight: 500 }}>
                    📎 Choose Files
                  </button>
                  {evidenceFiles.length > 0 && (
                    <button type="button" disabled={evidenceUploading}
                      onClick={async () => {
                        setEvidenceUploading(true)
                        setEvidenceUploadError('')
                        try {
                          const formData = new FormData()
                          evidenceFiles.forEach(f => formData.append('files', f))
                          await fetchJson(`/api/tickets/ticket/add-attachments/${ticketId}/?attachment_type=evidence`, { method: 'POST', body: formData })
                          setEvidenceFiles([])
                          setEvidenceRefresh(n => n + 1)
                        } catch (err) {
                          setEvidenceUploadError(err?.message || 'Upload failed. Please try again.')
                        } finally {
                          setEvidenceUploading(false)
                        }
                      }}
                      style={{ padding: '7px 16px', borderRadius: 7, border: 'none', background: '#0090D9', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                      {evidenceUploading ? 'Uploading…' : `Upload ${evidenceFiles.length} file${evidenceFiles.length > 1 ? 's' : ''}`}
                    </button>
                  )}
                </div>
                {evidenceFiles.length > 0 && (
                  <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {evidenceFiles.map((f, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', background: '#fff', borderRadius: 6, border: '1px solid #E2E8F0' }}>
                        <span style={{ fontSize: 12, flex: 1, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📄 {f.name}</span>
                        <span style={{ fontSize: 11, color: '#9CA3AF', flexShrink: 0 }}>
                          {f.size < 1024 * 1024 ? `${(f.size / 1024).toFixed(0)} KB` : `${(f.size / (1024 * 1024)).toFixed(1)} MB`}
                        </span>
                        <button type="button" onClick={() => setEvidenceFiles(prev => prev.filter((_, j) => j !== i))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: 18, padding: 0, lineHeight: 1 }}>×</button>
                      </div>
                    ))}
                  </div>
                )}
                {evidenceUploadError && <div style={{ marginTop: 8, fontSize: 12, color: '#DC2626' }}>{evidenceUploadError}</div>}
              </div>

              {attachmentsLoading ? (
                <div className="td-tab-placeholder">Loading attachments…</div>
              ) : attachments.length === 0 ? (
                <div className="td-tab-placeholder">No attachments for this ticket.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {attachments.map((att, i) => {
                    const fileName = att.file_name ? att.file_name.split('/').pop() : `attachment-${i + 1}`
                    const ext = fileName.split('.').pop()?.toLowerCase()
                    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)
                    return (
                      <div key={att.id || i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8 }}>
                        <span style={{ fontSize: 20, flexShrink: 0 }}>{isImage ? '🖼️' : '📄'}</span>
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: '#1E293B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fileName}</div>
                        </div>
                        <a href={att.file} target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: 12, fontWeight: 600, color: '#0090D9', textDecoration: 'none', flexShrink: 0, padding: '4px 10px', border: '1px solid #BAE6FD', borderRadius: 6, background: '#F0F9FF' }}>
                          Download ↗
                        </a>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <aside className="td-sidebar" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <TatDetails tat={tat} stage={info.ticket_stage} />

          {/* Close Ticket / Resolution collapsible */}
          <div style={{ border: '1px solid #E0E7EF', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
            <button
              onClick={() => setCloseFormOpen(o => !o)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 14px',
                background: closeFormOpen ? (showReadOnly ? '#F0F9FF' : '#FFFBEB') : '#fff',
                border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#1E293B',
                borderBottom: closeFormOpen ? '1px solid #E0E7EF' : 'none',
              }}
            >
              <span>{showReadOnly ? '✓ Resolution Details' : 'Close Ticket'}</span>
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="#64748B" strokeWidth="2"
                style={{ transform: closeFormOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s' }}>
                <path d="M5 8l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {closeFormOpen && (
              showReadOnly ? (
                /* ── Read-only resolution details ── */
                <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {resolutionData.resolution_fields && Object.keys(resolutionData.resolution_fields).length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {Object.entries(resolutionData.resolution_fields).map(([key, value]) => (
                        <div key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                          <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500, flexShrink: 0 }}>{formatFieldLabel(key)}</span>
                          <span style={{ fontSize: 13, color: '#111827', fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>{String(value || '—')}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ borderTop: '1px solid #E2E8F0' }} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>Ticket Status</span>
                      <span style={{ fontSize: 13, color: '#111827', fontWeight: 600 }}>{displayStatus(resolutionData.ticket_status)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>Issue Resolved</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: resolutionData.issue_resolved === 'yes' ? '#16A34A' : resolutionData.issue_resolved === 'no' ? '#DC2626' : '#6B7280' }}>
                        {resolutionData.issue_resolved === 'yes' ? 'Yes' : resolutionData.issue_resolved === 'no' ? 'No' : '—'}
                      </span>
                    </div>
                    {resolutionData.remarks && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 500 }}>Remarks</span>
                        <div style={{ fontSize: 13, color: '#111827', lineHeight: 1.5 }}>{resolutionData.remarks}</div>
                      </div>
                    )}
                  </div>
                  {resolutionData.attachment_type && (
                    <>
                      <div style={{ borderTop: '1px solid #E2E8F0' }} />
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '.4px' }}>
                        {formatFieldLabel(resolutionData.attachment_type)}
                      </div>
                      {resolutionAttachmentsLoading ? (
                        <div style={{ fontSize: 12, color: '#94A3B8' }}>Loading…</div>
                      ) : resolutionAttachments.length === 0 ? (
                        <div style={{ fontSize: 12, color: '#94A3B8' }}>No attachments uploaded.</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {resolutionAttachments.map((att, i) => {
                            const fileName = att.file_name ? att.file_name.split('/').pop() : `attachment-${i + 1}`
                            const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileName.split('.').pop()?.toLowerCase())
                            return (
                              <div key={att.id || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: '#F8FAFC', borderRadius: 8, border: '1px solid #E2E8F0' }}>
                                <span style={{ fontSize: 18, flexShrink: 0 }}>{isImage ? '🖼️' : '📄'}</span>
                                <span style={{ fontSize: 12, flex: 1, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fileName}</span>
                                <a href={att.file} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, fontWeight: 600, color: '#0090D9', textDecoration: 'none', flexShrink: 0 }}>
                                  {isImage ? 'View' : 'Download'} ↗
                                </a>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                /* ── Close form (ticket open) ── */
                <div style={{ padding: '0 0 8px' }}>
                  {closeError && (
                    <div style={{ margin: '10px 14px 0', padding: '8px 12px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 7, fontSize: 12, color: '#DC2626' }}>
                      {closeError}
                    </div>
                  )}
                  {closeDone ? (
                    <div style={{ margin: '10px 14px 16px', padding: '10px 14px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, fontSize: 13, color: '#16A34A', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                        <circle cx="10" cy="10" r="9" stroke="#16A34A" strokeWidth="1.5"/>
                        <path d="M6 10l3 3 5-5" stroke="#16A34A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Submitted successfully. Reloading…
                    </div>
                  ) : (
                    <CloseFormComponent
                      ticket={detail}
                      ticketId={String(ticketId)}
                      resolvedData={hasResolutionData ? resolutionData : null}
                      readOnly={false}
                      team={resolutionTeam}
                      onSubmit={handleCloseSubmit}
                      submitting={closeSubmitting}
                    />
                  )}
                </div>
              )
            )}
          </div>

          <QuickActions
            status={statusValue}
            ticketId={ticketId}
            fetchJson={fetchJson}
            onStatusUpdated={(s) => { setStatusValue(s); setDetailRefresh(n => n + 1) }}
          />
        </aside>
      </div>
    </div>
  )
}
